import ChainLogo from '@/components/icons/ChainLogo'
import TokenLogo from '@/components/token-logo'
import StackTokenLogo from '@/components/token-logo/StackTokenLogo'
import { ChartConfig, ChartContainer } from '@/components/ui/chart'
import { SorteableButton } from '@/components/ui/data-table'
import { isInactiveDTF } from '@/hooks/use-dtf-status'
import { type IndexDTFItem } from '@/hooks/useIndexDTFList'
import { cn } from '@/lib/utils'
import { formatCurrency, getFolioRoute } from '@/utils'
import {
  getPerformanceDirection as getSeriesPerformanceDirection,
  PERFORMANCE_COLORS,
  type PerformanceDirection,
} from '@/utils/chart-performance-colors'
import { Trans } from '@lingui/react/macro'
import { ColumnDef } from '@tanstack/react-table'
import { useId } from 'react'
import { Link } from 'react-router-dom'
import { Line, LineChart, YAxis } from 'recharts'
import { BasketHoverCard } from './basket-hover-card'
import { calculatePercentageChange } from './utils'

export const LIMIT_ASSETS = 4
const HOVER_LIMIT_ASSETS = 10

const chartConfig = {
  desktop: {
    label: 'Desktop',
    color: 'hsl(var(--card))',
  },
} satisfies ChartConfig

const TableHeader = ({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) => <div className={cn('text-legend', className)}>{children}</div>

const exposureTriggerClassName =
  'inline-flex items-center gap-1 rounded-full border border-transparent p-1.5 transition-[border-color,background-color,opacity] duration-150 group-hover/dtf-row:border-secondary group-hover/dtf-row:bg-card'

const withChainId = (
  assets: IndexDTFItem['basket'],
  chainId: IndexDTFItem['chainId']
) => assets.map((asset) => ({ ...asset, chain: chainId }))

const formatPriceChangePercent = (dtf: IndexDTFItem) => {
  if (typeof dtf.priceChange?.percent !== 'number') {
    return calculatePercentageChange(dtf.performance)
  }

  const percent = dtf.priceChange.percent
  return `${percent > 0 ? '+' : ''}${percent.toFixed(2)}%`
}

const getDtfPerformanceDirection = (
  dtf: IndexDTFItem
): PerformanceDirection => {
  if (typeof dtf.priceChange?.percent === 'number') {
    if (dtf.priceChange.percent > 0) return 'positive'
    if (dtf.priceChange.percent < 0) return 'negative'
    return 'neutral'
  }

  return getSeriesPerformanceDirection(dtf.performance)
}

const getSparklineValueDomain = ([dataMin, dataMax]: [number, number]): [
  number,
  number,
] => {
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return [dataMin, dataMax]
  }

  if (dataMin === dataMax) {
    const padding = Math.max(Math.abs(dataMin) * 0.01, 0.01)
    return [dataMin - padding, dataMax + padding]
  }

  const padding = (dataMax - dataMin) * 0.08
  return [dataMin - padding, dataMax + padding]
}

const PerformanceCell = ({ dtf }: { dtf: IndexDTFItem }) => {
  const performance = dtf.performance
  const percentageChange = formatPriceChangePercent(dtf)
  const performanceDirection = getDtfPerformanceDirection(dtf)
  const performanceColorClassName = cn(
    performanceDirection === 'positive' && 'text-success',
    performanceDirection === 'negative' && 'text-destructive'
  )
  const strokeGradientId = `${useId().replace(/:/g, '')}-performance-stroke`
  const lineStroke =
    performanceDirection === 'positive'
      ? `url(#${strokeGradientId})`
      : 'currentColor'

  return (
    <div className="flex items-center justify-end gap-4">
      <div className="text-right">
        {percentageChange ? (
          <span className={performanceColorClassName}>{percentageChange}</span>
        ) : (
          <span className="text-legend">
            <Trans>No data</Trans>
          </span>
        )}
      </div>
      <div className="h-10 w-[90px]">
        {performance.length > 1 && (
          <ChartContainer
            config={chartConfig}
            className={cn('h-10 w-[90px]', performanceColorClassName)}
          >
            <LineChart
              data={performance}
              margin={{ top: 3, right: 2, bottom: 3, left: 2 }}
            >
              {performanceDirection === 'positive' && (
                <defs>
                  <linearGradient
                    id={strokeGradientId}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop
                      offset="0%"
                      stopColor={PERFORMANCE_COLORS.positive.start}
                    />
                    <stop
                      offset="100%"
                      stopColor={PERFORMANCE_COLORS.positive.end}
                    />
                  </linearGradient>
                </defs>
              )}
              <YAxis hide visibility="0" domain={getSparklineValueDomain} />
              <Line
                type="linear"
                dataKey="value"
                stroke={lineStroke}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ChartContainer>
        )}
      </div>
    </div>
  )
}

export const indexDTFColumns: ColumnDef<IndexDTFItem>[] = [
  {
    header: ({ column }) => (
      <SorteableButton column={column}>
        <Trans>Name</Trans>
      </SorteableButton>
    ),
    accessorKey: 'name',
    meta: {
      className: 'w-[500px] max-w-[500px]',
    },
    cell: ({ row }) => (
      <Link
        to={getFolioRoute(row.original.address, row.original.chainId)}
        className="flex min-w-0 items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0">
          <TokenLogo src={row.original.brand?.icon || undefined} size="xl" />
          <ChainLogo
            chain={row.original.chainId}
            className="absolute -bottom-1 -right-1 rounded-md border-2 border-card bg-card"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center gap-1 break-words pt-0.5">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold leading-tight">
              {row.original.name}
              <span className="ml-2 font-light text-legend/70">
                ${row.original.symbol}
              </span>
            </h4>
            {isInactiveDTF(row.original.status) && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-600 dark:text-yellow-400">
                <Trans>Inactive</Trans>
              </span>
            )}
          </div>
          <span className="text-sm leading-tight text-legend">
            {row.original.brand?.tags?.length ? (
              row.original.brand.tags.join(', ')
            ) : (
              <Trans>No tags</Trans>
            )}
          </span>
        </div>
      </Link>
    ),
  },
  {
    header: () => (
      <TableHeader>
        <Trans>Exposure</Trans>
      </TableHeader>
    ),
    accessorKey: 'basket',
    meta: {
      className: 'w-[180px] min-w-[180px] pr-2',
    },
    cell: ({ row }) => {
      const basket = row.original.basket
      const hiddenAssetCount = Math.max(0, basket.length - LIMIT_ASSETS)
      const hoverHiddenAssetCount = Math.max(
        0,
        basket.length - HOVER_LIMIT_ASSETS
      )
      const cappedBacking = withChainId(
        basket.slice(0, LIMIT_ASSETS),
        row.original.chainId
      )
      const hoverBacking = withChainId(
        basket.slice(0, HOVER_LIMIT_ASSETS),
        row.original.chainId
      )

      return (
        <div className="relative flex h-10 w-full items-center">
          <BasketHoverCard indexDTF={row.original}>
            <div
              className={cn(
                exposureTriggerClassName,
                hiddenAssetCount > 0 && 'group-hover/dtf-row:opacity-0'
              )}
            >
              <StackTokenLogo
                className="[&>div]:rounded-full [&>div]:border-2 [&>div]:border-card [&>div]:bg-card"
                tokens={cappedBacking}
                overlap={2}
                size={24}
                reverseStack
                outsource
              />
              {hiddenAssetCount > 0 && (
                <span className="inline-flex w-6 justify-center text-sm text-legend">
                  +{hiddenAssetCount}
                </span>
              )}
            </div>
          </BasketHoverCard>
          {hiddenAssetCount > 0 && (
            <BasketHoverCard indexDTF={row.original}>
              <div
                className={cn(
                  exposureTriggerClassName,
                  'absolute left-0 top-1/2 hidden -translate-y-1/2 group-hover/dtf-row:flex'
                )}
              >
                <StackTokenLogo
                  className="[&>div]:rounded-full [&>div]:border-2 [&>div]:border-card [&>div]:bg-card"
                  tokens={hoverBacking}
                  overlap={2}
                  size={24}
                  reverseStack
                  outsource
                />
                {hoverHiddenAssetCount > 0 && (
                  <span className="inline-flex w-6 justify-center text-sm text-legend">
                    +{hoverHiddenAssetCount}
                  </span>
                )}
              </div>
            </BasketHoverCard>
          )}
        </div>
      )
    },
  },
  {
    header: ({ column }) => (
      <TableHeader className="text-right">
        <SorteableButton column={column}>
          <Trans>Market Cap</Trans>
        </SorteableButton>
      </TableHeader>
    ),
    accessorKey: 'marketCap',
    cell: ({ row }) => (
      <div className="flex items-center justify-end">
        <div>
          $
          {formatCurrency(row.original.marketCap, 1, {
            notation: 'compact',
            compactDisplay: 'short',
            minimumFractionDigits: 0,
          })}
        </div>
      </div>
    ),
  },
  {
    header: ({ column }) => (
      <TableHeader className="text-right">
        <SorteableButton column={column}>
          <Trans>Price</Trans>
        </SorteableButton>
      </TableHeader>
    ),
    accessorKey: 'price',
    meta: {
      className: 'pl-10 text-right',
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-end">
        <div>${formatCurrency(row.original.price, 5)}</div>
      </div>
    ),
  },
  {
    header: ({ column }) => (
      <TableHeader className="text-right">
        <SorteableButton column={column}>
          <Trans>Performance (Last 30 Days)</Trans>
        </SorteableButton>
      </TableHeader>
    ),
    accessorKey: 'performancePercent',
    meta: {
      className: 'text-right',
    },
    cell: ({ row }) => <PerformanceCell dtf={row.original} />,
  },
]
